<template>
    <div
        v-if="hasStoreCredit"
        class="max-w-[650px] bg-gray-100 p-4"
    >
        <div class="flex items-center justify-between">
            <div class="font-medium">
                {{
                    t('checkoutSections.storeCreditAvailable', {
                        amount: storeCreditBalance.formatCurrency()
                    })
                }}
            </div>

            <div>
                <prime-button
                    v-if="!editingStoreCredit"
                    icon="pi pi-fw pi-wallet"
                    outlined
                    :label="t('checkoutSections.useStoreCredit')"
                    :disabled="isProcessing"
                    @click="handleUseStoreCredit"
                />
            </div>
        </div>

        <div
            v-if="editingStoreCredit"
            class="mt-4"
        >
            <label for="storeCreditBalanceUsed">{{
                t('checkoutSections.amountToUse')
            }}</label>
            <div class="flex gap-2">
                <div>
                    <input-number
                        v-model="storeCreditBalanceUsed"
                        input-id="storeCreditBalanceUsed"
                        placeholder="0.00"
                        mode="currency"
                        :max="Number(maxStoreCreditUsed.toFixed(2))"
                        :min="0"
                        :currency="currencyFormat"
                        :locale="locale"
                        :disabled="isProcessing"
                        @focus="selectStoreCreditBalanceUsed"
                    />
                </div>

                <prime-button
                    icon="pi pi-fw pi-times"
                    :label="t('checkoutSections.remove')"
                    outlined
                    severity="warning"
                    :disabled="isProcessing"
                    @click="handleCancelStoreCredit"
                />
            </div>
            <div class="mt-2 text-sm">
                {{
                    t('checkoutSections.remainingAfterPurchase', {
                        amount: storeCreditRemaining.formatCurrency()
                    })
                }}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const StoreCreditPaymentCustomerFragment = graphql(`
    fragment StoreCreditPaymentCustomerFragment on Customer {
        creditBalance
        onlineStoreCreditEnabled
    }
`)
</script>

<script setup lang="ts">
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import { computed, nextTick, watch } from 'vue'
import { Decimal } from '~/composables/support/decimal'
import PrimeButton from 'primevue/button'
import { useField } from 'vee-validate'
import type { StoreCreditPaymentInfo } from '~/components/Checkout/Sections/PaymentInformation.vue'
import InputNumber from 'primevue/inputnumber'
import { useLocalization } from '~/composables/use-localization'
import { useUpdateCheckoutStoreCreditData } from '~/components/Checkout/Sections/Payment/checkout-payment-data'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    customer: FragmentType<typeof StoreCreditPaymentCustomerFragment>
    paymentSourceId: string
    afterGiftCardTotal: Decimal
}>()

const { t } = useI18n({ useScope: 'global' })

const { currencyFormat, locale } = useLocalization()

const customerData = computed(() => {
    return getFragmentData(StoreCreditPaymentCustomerFragment, props.customer)
})

const storeCreditBalance = computed(() => {
    return new Decimal(customerData.value.creditBalance).abs()
})

const hasStoreCredit = computed(() => {
    return (
        customerData.value.onlineStoreCreditEnabled &&
        storeCreditBalance.value.isNotZero()
    )
})

const maxStoreCreditUsed = computed(() => {
    const maximum = Decimal.min(
        storeCreditBalance.value,
        props.afterGiftCardTotal
    )

    return Decimal.max(Decimal.zero(), maximum)
})

const { value: storeCredit } = useField<StoreCreditPaymentInfo>(
    'payment.storeCredit',
    '',
    {
        validateOnValueUpdate: false
    }
)

const storeCreditBalanceUsed = computed({
    get() {
        if (storeCredit.value === null) {
            return null
        }

        if (storeCredit.value.amountUsed === null) {
            return null
        }

        return Number(storeCredit.value.amountUsed)
    },
    set(value) {
        if (storeCredit.value === null) {
            return
        }

        storeCredit.value.amountUsed = value === null ? null : String(value)
    }
})

watch(maxStoreCreditUsed, newValue => {
    if (storeCredit.value !== null && storeCredit.value.amountUsed !== null) {
        const amount = new Decimal(storeCredit.value.amountUsed)
        if (newValue.isLessThan(amount)) {
            storeCreditBalanceUsed.value = Number(newValue.toFixed(2))
            if (newValue.isZero()) {
                handleCancelStoreCredit()
            }
        }
    }
})

const storeCreditRemaining = computed(() => {
    if (storeCredit.value === null) {
        return storeCreditBalance.value
    }

    if (storeCredit.value.amountUsed === null) {
        return storeCreditBalance.value
    }

    return storeCreditBalance.value.minus(
        new Decimal(storeCredit.value.amountUsed)
    )
})

function selectStoreCreditBalanceUsed() {
    const input = document.getElementById(
        'storeCreditBalanceUsed'
    ) as HTMLInputElement | null

    input?.select()
}

const editingStoreCredit = computed(() => {
    return storeCredit.value !== null
})
async function handleUseStoreCredit() {
    storeCredit.value = {
        paymentSourceId: props.paymentSourceId,
        amountUsed: maxStoreCreditUsed.value.toFixed(2)
    }
    await nextTick()
    selectStoreCreditBalanceUsed()
}

function handleCancelStoreCredit() {
    storeCredit.value = null
}

const updateStoreCredit = useUpdateCheckoutStoreCreditData()
watch(
    () => storeCredit.value,
    newValue => {
        updateStoreCredit({
            amountUsed: newValue?.amountUsed ?? '0'
        })
    },
    {
        deep: true
    }
)

const { isProcessing } = useCheckoutFormProcessing()
</script>
